"use client";

import { useEffect, useState } from "react";
import Icon from "@/components/ui/AppIcon";
import { useInView } from "react-intersection-observer";
import { motion } from "framer-motion";

const metrics = [
  {
    value: "50,000+",
    label: "Orders Fulfilled Monthly",
    icon: "CubeIcon",
  },
  {
    value: "5–8 days",
    label: "Global Delivery Time",
    icon: "TruckIcon",
  },
  {
    value: "< 2 hrs",
    label: "Average Support Response",
    icon: "ChatBubbleLeftRightIcon",
  },
  {
    value: "98.4%",
    label: "On-Time Dispatch Rate",
    icon: "ChartBarIcon",
  },
];

export default function MetricsSection() {
  const [animated, setAnimated] = useState(false);
  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      setAnimated(true);
    }
  }, [inView]);

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <section ref={ref} className="py-20 bg-deep-dark border-y border-divider">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Logo cloud */}
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <p className="text-xs font-bold tracking-widest uppercase text-text-secondary mb-6 font-heading">
            Trusted by ecommerce brands worldwide
          </p>
          <div className="flex flex-wrap items-center justify-center gap-8 opacity-40">
            {["Shopify", "WooCommerce", "TikTok Shop", "Amazon", "Etsy", "BigCommerce"]?.map((brand, i) => (
              <motion.span 
                key={brand} 
                className="text-sm font-extrabold font-heading text-text-secondary tracking-tight"
                initial={{ opacity: 0, x: -20 }}
                animate={inView ? { opacity: 0.4, x: 0 } : {}}
                transition={{ duration: 0.5, delay: i * 0.1 }}
              >
                {brand}
              </motion.span>
            ))}
          </div>
        </motion.div>

        {/* Metrics grid */}
        <motion.div 
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {metrics?.map((metric, idx) => (
            <motion.div
              key={idx}
              variants={itemVariants}
              className="glass-card p-8 text-center group"
            >
              <div className="w-12 h-12 rounded-2xl bg-glass-white border border-glass-border flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-smooth">
                <Icon name={metric?.icon} size={24} className="text-accent-primary" />
              </div>
              <div className="text-3xl font-bold text-text-primary mb-2">{metric?.value}</div>
              <div className="text-sm text-text-secondary font-medium">{metric?.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}